// PaymentManagementDialog.jsx - Improved UI
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  // DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Divider,
  InputAdornment,
  Stack,
  CircularProgress,
  Tab,
  Tabs,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  alpha,
  useTheme,
  Fade,
  Zoom,
  Slide
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Payment as PaymentIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Scale as ScaleIcon,
  ExpandMore as ExpandMoreIcon,
  Money as MoneyIcon,
  // AccountBalance as AccountBalanceIcon,
  // LocalAtm as LocalAtmIcon,
  // CreditCard as CreditCardIcon,
  // ArrowForward as ArrowForwardIcon,
  // TrendingUp as TrendingUpIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const PaymentManagementDialog = ({
  open,
  onClose,
  transaction,
  customer,
  onPaymentComplete,
  recordPayment
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Money Payment States
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [paymentModes, setPaymentModes] = useState({
    cash: '',
    online: '',
    cheque: '',
    other: ''
  });
  const [paymentNote, setPaymentNote] = useState('');
  const [remainingBalance, setRemainingBalance] = useState(0);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [originalAmount, setOriginalAmount] = useState(0);

  // Fine Payment States
  const [fineWeight, setFineWeight] = useState('');
  const [finePurity, setFinePurity] = useState('');
  const [fineNote, setFineNote] = useState('');
  const [remainingFineBalance, setRemainingFineBalance] = useState(0);
  const [totalFineWeight, setTotalFineWeight] = useState(0);
  const [finePaymentHistory, setFinePaymentHistory] = useState([]);
  const [calculatedFine, setCalculatedFine] = useState(0);
  const [fineLoading, setFineLoading] = useState(false);

  // UI State
  const [activeTab, setActiveTab] = useState(0);
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    if (open) {
      setAnimateIn(true);
    }
  }, [open]);

  useEffect(() => {
    if (transaction && open) {
      const balance = transaction.transactionDetails?.balanceDue ||
        (transaction.summary?.grandTotal - transaction.summary?.paymentDone);
      const original = transaction.transactionDetails?.originalBillAmount ||
        transaction.summary?.grandTotal || 0;
      setRemainingBalance(Math.max(0, Number(balance)));
      setOriginalAmount(Number(original));

      const history = transaction.paymentDetails?.paymentHistory || [];
      setPaymentHistory(history);

      const fineBalance = transaction.calculatedTotals?.fineBalance || 0;
      const totalFine = transaction.calculatedTotals?.totalFineWeight || 0;
      setRemainingFineBalance(Math.max(0, Number(fineBalance)));
      setTotalFineWeight(Number(totalFine));

      const finePayments = transaction.paymentDetails?.finePayment?.payments || [];
      setFinePaymentHistory(finePayments);

      const hasMoneyPending = remainingBalance > 0;
      const hasFinePending = fineBalance > 0;

      if (hasMoneyPending && !hasFinePending) {
        setActiveTab(0);
      } else if (!hasMoneyPending && hasFinePending) {
        setActiveTab(1);
      } else if (hasMoneyPending && hasFinePending) {
        setActiveTab(0);
      }
    }
  }, [transaction, open, remainingBalance]);

  useEffect(() => {
    if (fineWeight && finePurity) {
      const weight = parseFloat(fineWeight) || 0;
      const purity = parseFloat(finePurity) || 0;
      const fine = weight * (purity / 100);
      setCalculatedFine(fine);
    } else {
      setCalculatedFine(0);
    }
  }, [fineWeight, finePurity]);

  const calculateTotalPayment = () => {
    return Object.values(paymentModes).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  };

  const handlePaymentModeChange = (mode, value) => {
    const newPaymentModes = { ...paymentModes, [mode]: value };
    setPaymentModes(newPaymentModes);
  };

  const validateMoneyPayment = () => {
    const totalPayment = calculateTotalPayment();
    if (totalPayment <= 0) {
      alert('Please enter payment amount');
      return false;
    }
    if (totalPayment > remainingBalance) {
      alert(`Payment cannot exceed remaining balance of ₹${remainingBalance.toFixed(2)}`);
      return false;
    }
    return true;
  };

  const validateFinePayment = () => {
    if (!fineWeight || parseFloat(fineWeight) <= 0) {
      alert('Please enter valid fine weight');
      return false;
    }
    if (!finePurity || parseFloat(finePurity) <= 0 || parseFloat(finePurity) > 100) {
      alert('Please enter valid purity percentage (1-100)');
      return false;
    }
    if (calculatedFine <= 0) {
      alert('Calculated fine amount must be greater than 0');
      return false;
    }
    if (calculatedFine > remainingFineBalance) {
      alert(`Fine payment cannot exceed remaining fine balance of ${remainingFineBalance.toFixed(3)} gm`);
      return false;
    }
    return true;
  };

  const handleSubmitMoneyPayment = async () => {
    if (!validateMoneyPayment()) return;
    setLoading(true);

    const day = paymentDate.getDate();
    const month = paymentDate.getMonth() + 1;
    const year = paymentDate.getFullYear();
    const paymentDateValue = `'${day}/${month}/${year}`;

    const paymentData = {
      type: 'money',
      amount: calculateTotalPayment(),
      date: paymentDateValue,
      paymentModes: {
        cash: parseFloat(paymentModes.cash) || 0,
        online: parseFloat(paymentModes.online) || 0,
        cheque: parseFloat(paymentModes.cheque) || 0,
        other: parseFloat(paymentModes.other) || 0,
        advanceBreakup: {}
      },
      note: paymentNote,
      recordedBy: "Cashier"
    };

    try {
      const result = await recordPayment(transaction.id, paymentData);
      if (result.success) {
        setPaymentModes({ cash: '', online: '', cheque: '', other: '' });
        setPaymentNote('');
        setPaymentDate(new Date());
        onPaymentComplete(result.data);
        const newRemainingBalance = remainingBalance - paymentData.amount;
        if (newRemainingBalance <= 0 && remainingFineBalance <= 0) {
          onClose();
        }
      } else {
        alert(result.message || 'Failed to record payment');
      }
    } catch (error) {
      console.error("Payment error:", error);
      alert(error.message || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFinePayment = async () => {
    if (!validateFinePayment()) return;
    setFineLoading(true);

    const day = paymentDate.getDate();
    const month = paymentDate.getMonth() + 1;
    const year = paymentDate.getFullYear();
    const fineDateValue = `${day}/${month}/${year}`;

    const fineData = {
      type: 'fine',
      weight: parseFloat(fineWeight) || 0,
      purity: parseFloat(finePurity) || 0,
      actualFine: calculatedFine,
      date: fineDateValue,
      note: fineNote,
      recordedBy: "Cashier"
    };

    try {
      const result = await recordPayment(transaction.id, fineData);
      if (result.success) {
        setFineWeight('');
        setFinePurity('');
        setFineNote('');
        setCalculatedFine(0);
        onPaymentComplete(result.data);
        const newRemainingFine = remainingFineBalance - calculatedFine;
        if (remainingBalance <= 0 && newRemainingFine <= 0) {
          onClose();
        }
      } else {
        alert(result.message || 'Failed to record fine payment');
      }
    } catch (error) {
      console.error("Fine payment error:", error);
      alert(error.message || 'Failed to record fine payment');
    } finally {
      setFineLoading(false);
    }
  };

  const totalPaid = paymentHistory.reduce((sum, p) => sum + p.amount, 0);
  const totalFinePaid = finePaymentHistory.reduce((sum, p) => sum + (p.actualFine || 0), 0);
  const hasMoneyPending = remainingBalance > 0;
  const hasFinePending = remainingFineBalance > 0;
  const showTabs = hasMoneyPending && hasFinePending;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={Zoom}
      TransitionProps={{ timeout: 400 }}
      PaperProps={{
        sx: {
          borderRadius: '24px',
          background: isDark
            ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 100%)'
            : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
          backdropFilter: 'blur(30px)',
          border: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', 0.2)}`,
          boxShadow: '0 25px 80px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
        }
      }}
    >
      {/* Animated Header */}
      <Slide direction="down" in={animateIn} timeout={400}>
        <Box
          sx={{
            p: 3,
            borderBottom: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', 0.1)}`,
            background: isDark
              ? 'linear-gradient(135deg, rgba(96, 165, 250, 0.15) 0%, rgba(59, 130, 246, 0.08) 100%)'
              : 'linear-gradient(135deg, rgba(37, 99, 235, 0.06) 0%, rgba(29, 78, 216, 0.03) 100%)',
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #3B82F6, #1D4ED8)',
                  boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)',
                }}
              >
                <PaymentIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={800} sx={{ color: isDark ? '#e2e8f0' : '#1e293b' }}>
                  Payment & Fine Management
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                  Bill #{transaction?.transactionDetails?.billNo}
                </Typography>
              </Box>
            </Box>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </Slide>

      <DialogContent sx={{ p: 3 }}>
        <Fade in={animateIn} timeout={500}>
          <Stack spacing={3}>
            {/* Bill Information Card */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: '20px',
                background: isDark
                  ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.6), rgba(15, 23, 42, 0.6))'
                  : 'linear-gradient(135deg, rgba(248, 250, 252, 0.8), rgba(255, 255, 255, 0.6))',
                border: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', 0.2)}`,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ReceiptIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                      <Typography variant="caption" color="text.secondary">Bill Number</Typography>
                    </Box>
                    <Typography variant="h6" fontWeight={700} color="#3b82f6">
                      {transaction?.transactionDetails?.billNo}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ReceiptIcon sx={{ fontSize: 16, color: '#64748b' }} />
                      <Typography variant="caption" color="text.secondary">Customer</Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={600}>
                      {customer?.name || 'N/A'}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: '12px', bgcolor: alpha('#3b82f6', 0.08) }}>
                    <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                    <Typography variant="h6" fontWeight={800} color="#3b82f6">
                      ₹{originalAmount.toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: '12px', bgcolor: alpha('#10b981', 0.08) }}>
                    <Typography variant="caption" color="text.secondary">Total Paid</Typography>
                    <Typography variant="h6" fontWeight={800} color="#10b981">
                      ₹{totalPaid.toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: '12px', bgcolor: alpha(hasMoneyPending ? '#ef4444' : '#10b981', 0.08) }}>
                    <Typography variant="caption" color="text.secondary">Pending Money</Typography>
                    <Typography variant="h6" fontWeight={800} color={hasMoneyPending ? '#ef4444' : '#10b981'}>
                      ₹{remainingBalance.toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                </Grid>
                {totalFineWeight > 0 && (
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: '12px', bgcolor: alpha(hasFinePending ? '#f59e0b' : '#10b981', 0.08) }}>
                      <Typography variant="caption" color="text.secondary">Pending Fine</Typography>
                      <Typography variant="h6" fontWeight={800} color={hasFinePending ? '#f59e0b' : '#10b981'}>
                        {remainingFineBalance.toFixed(3)} gm
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Combined Progress Bars */}
            <Box>
              <Typography variant="caption" fontWeight={600} color="text.secondary" gutterBottom>
                Payment Progress
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: totalFineWeight > 0 ? 2 : 0 }}>
                <Box sx={{ flex: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={originalAmount > 0 ? (totalPaid / originalAmount) * 100 : 0}
                    sx={{ height: 8, borderRadius: 4, bgcolor: alpha('#3b82f6', 0.2), '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: '#3b82f6' } }}
                  />
                </Box>
                <Typography variant="body2" fontWeight={600} color="#3b82f6">
                  {originalAmount > 0 ? ((totalPaid / originalAmount) * 100).toFixed(1) : 0}%
                </Typography>
              </Box>
              {totalFineWeight > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={(totalFinePaid / totalFineWeight) * 100}
                      sx={{ height: 8, borderRadius: 4, bgcolor: alpha('#f59e0b', 0.2), '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: '#f59e0b' } }}
                    />
                  </Box>
                  <Typography variant="body2" fontWeight={600} color="#f59e0b">
                    {((totalFinePaid / totalFineWeight) * 100).toFixed(1)}%
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Tabs */}
            {showTabs && (
              <Tabs
                value={activeTab}
                onChange={(e, v) => setActiveTab(v)}
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', minHeight: 48 },
                  '& .Mui-selected': { color: '#3b82f6' },
                  '& .MuiTabs-indicator': { backgroundColor: '#3b82f6', height: 3 }
                }}
              >
                <Tab icon={<MoneyIcon />} label="Money Payment" />
                <Tab icon={<ScaleIcon />} label="Fine Collection" />
              </Tabs>
            )}

            {/* Money Payment Section */}
            {(activeTab === 0 || !showTabs) && hasMoneyPending && (
              <Zoom in={activeTab === 0 || !showTabs} timeout={300}>
                <Paper
                  sx={{
                    p: 2.5,
                    borderRadius: '20px',
                    background: isDark ? 'rgba(30, 41, 59, 0.4)' : 'rgba(248, 250, 252, 0.6)',
                    border: `1px solid ${alpha(isDark ? '#475569' : '#e2e8f0', 0.2)}`,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MoneyIcon sx={{ fontSize: 18, color: '#10b981' }} />
                    Record Money Payment
                  </Typography>

                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                      <DatePicker
                        label="Payment Date"
                        value={paymentDate}
                        onChange={setPaymentDate}
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            sx: { '& .MuiOutlinedInput-root': { borderRadius: '12px' } }
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        label="Cash"
                        type="number"
                        value={paymentModes.cash}
                        onChange={(e) => handlePaymentModeChange('cash', e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="0"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                          inputProps: { min: 0, step: "1" }
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Online"
                        type="number"
                        value={paymentModes.online}
                        onChange={(e) => handlePaymentModeChange('online', e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="0"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                          inputProps: { min: 0, step: "1" }
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Cheque"
                        type="number"
                        value={paymentModes.cheque}
                        onChange={(e) => handlePaymentModeChange('cheque', e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="0"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                          inputProps: { min: 0, step: "1" }
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Other"
                        type="number"
                        value={paymentModes.other}
                        onChange={(e) => handlePaymentModeChange('other', e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="0"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                          inputProps: { min: 0, step: "1" }
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Note (Optional)"
                        value={paymentNote}
                        onChange={(e) => setPaymentNote(e.target.value)}
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                        placeholder="e.g., Partial payment, Cheque number, etc."
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Alert
                        severity="info"
                        icon={<InfoIcon />}
                        sx={{
                          borderRadius: '12px',
                          background: alpha('#3b82f6', 0.08),
                          border: `1px solid ${alpha('#3b82f6', 0.2)}`,
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Total Payment</Typography>
                          <Typography variant="body2" fontWeight={700} color="#3b82f6">
                            ₹{calculateTotalPayment().toLocaleString('en-IN')}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mt={0.5}>
                          <Typography variant="caption" color="text.secondary">Remaining after payment</Typography>
                          <Typography variant="caption" fontWeight={600} color={remainingBalance - calculateTotalPayment() > 0 ? '#ef4444' : '#10b981'}>
                            ₹{Math.max(0, remainingBalance - calculateTotalPayment()).toLocaleString('en-IN')}
                          </Typography>
                        </Stack>
                      </Alert>
                    </Grid>
                  </Grid>

                  {/* Payment History Accordion */}
                  {paymentHistory.length > 0 && (
                    <Accordion
                      sx={{
                        mt: 2,
                        borderRadius: '12px',
                        background: 'transparent',
                        '&:before': { display: 'none' },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="caption" fontWeight={600} display="flex" alignItems="center" gap={1}>
                          <HistoryIcon fontSize="small" />
                          Payment History ({paymentHistory.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell>Payment Modes</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {paymentHistory.slice(0, 3).map((payment, index) => (
                                <TableRow key={payment.id}>
                                  <TableCell>{payment?.date?.toString()?.slice(1)}</TableCell>
                                  <TableCell align="right">₹{payment.amount.toFixed(2)}</TableCell>
                                  <TableCell>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                      {payment.paymentModes?.cash > 0 && <Chip size="small" label={`Cash: ₹${payment.paymentModes.cash}`} />}
                                      {payment.paymentModes?.online > 0 && <Chip size="small" label={`Online: ₹${payment.paymentModes.online}`} />}
                                      {payment.paymentModes?.cheque > 0 && <Chip size="small" label={`Cheque: ₹${payment.paymentModes.cheque}`} />}
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              ))}
                              {paymentHistory.length > 3 && (
                                <TableRow>
                                  <TableCell colSpan={3} align="center">
                                    <Typography variant="caption" color="text.secondary">+ {paymentHistory.length - 3} more payments</Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Paper>
              </Zoom>
            )}

            {/* Fine Collection Section */}
            {(activeTab === 1 || !showTabs) && hasFinePending && (
              <Zoom in={activeTab === 1 || !showTabs} timeout={300}>
                <Paper
                  sx={{
                    p: 2.5,
                    borderRadius: '20px',
                    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.08), rgba(124, 58, 237, 0.04))',
                    border: `1px solid ${alpha('#8b5cf6', 0.3)}`,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScaleIcon sx={{ fontSize: 18, color: '#8b5cf6' }} />
                    Record Fine Collection
                  </Typography>

                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                      <DatePicker
                        label="Collection Date"
                        value={paymentDate}
                        onChange={setPaymentDate}
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            sx: { '& .MuiOutlinedInput-root': { borderRadius: '12px' } }
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        label="Fine Weight (grams)"
                        type="number"
                        value={fineWeight}
                        onChange={(e) => setFineWeight(e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="Enter weight"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">gm</InputAdornment>,
                          inputProps: { step: "0.001", min: 0 }
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="Purity (%)"
                        type="number"
                        value={finePurity}
                        onChange={(e) => setFinePurity(e.target.value)}
                        fullWidth
                        size="small"
                        placeholder="Enter purity"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          inputProps: { step: "0.1", min: 0, max: 100 }
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>

                    {calculatedFine > 0 && (
                      <Grid item xs={12}>
                        <Alert severity="success" sx={{ borderRadius: '12px' }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2">Actual Fine to deduct:</Typography>
                            <Typography variant="body2" fontWeight={700}>{calculatedFine.toFixed(3)} gm</Typography>
                          </Stack>
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                            {fineWeight || 0} gm × ({finePurity || 0}% ÷ 100)
                          </Typography>
                        </Alert>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <TextField
                        label="Note (Optional)"
                        value={fineNote}
                        onChange={(e) => setFineNote(e.target.value)}
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                        placeholder="e.g., Fine payment details"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ borderRadius: '12px' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Fine to Collect</Typography>
                          <Typography variant="body2" fontWeight={700} color="#f59e0b">
                            {calculatedFine > 0 ? `${calculatedFine.toFixed(3)} gm` : 'Enter details'}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mt={0.5}>
                          <Typography variant="caption" color="text.secondary">Remaining fine balance</Typography>
                          <Typography variant="caption" fontWeight={600} color={remainingFineBalance - calculatedFine > 0 ? '#f59e0b' : '#10b981'}>
                            {(remainingFineBalance - calculatedFine).toFixed(3)} gm
                          </Typography>
                        </Stack>
                      </Alert>
                    </Grid>
                  </Grid>

                  {/* Fine Payment History Accordion */}
                  {finePaymentHistory.length > 0 && (
                    <Accordion sx={{ mt: 2, borderRadius: '12px', background: 'transparent', '&:before': { display: 'none' } }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="caption" fontWeight={600} display="flex" alignItems="center" gap={1}>
                          <ScaleIcon fontSize="small" />
                          Fine History ({finePaymentHistory.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell align="right">Weight</TableCell>
                                <TableCell align="right">Fine</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {finePaymentHistory.slice(0, 3).map((payment) => (
                                <TableRow key={payment.id}>
                                  <TableCell>{payment.date?.toString()?.replace(/^'/, '') || '-'}</TableCell>
                                  <TableCell align="right">{payment.weight?.toFixed(3)} gm</TableCell>
                                  <TableCell align="right">{payment.actualFine?.toFixed(3)} gm</TableCell>
                                </TableRow>
                              ))}
                              {finePaymentHistory.length > 3 && (
                                <TableRow>
                                  <TableCell colSpan={3} align="center">
                                    <Typography variant="caption" color="text.secondary">+ {finePaymentHistory.length - 3} more</Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Paper>
              </Zoom>
            )}

            {/* Fully Paid Message */}
            {!hasMoneyPending && !hasFinePending && (
              <Fade in={true}>
                <Alert
                  severity="success"
                  icon={<CheckCircleIcon />}
                  sx={{
                    borderRadius: '16px',
                    background: alpha('#10b981', 0.1),
                    border: `1px solid ${alpha('#10b981', 0.3)}`,
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>✓ Bill Fully Paid!</Typography>
                  <Typography variant="caption">No further payments or fine collections needed.</Typography>
                </Alert>
              </Fade>
            )}
          </Stack>
        </Fade>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2, gap: 2 }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: '12px',
            px: 3,
            py: 1,
            color: isDark ? '#94a3b8' : '#64748b',
            '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }
          }}
        >
          Close
        </Button>
        {activeTab === 0 && hasMoneyPending && (
          <Button
            onClick={handleSubmitMoneyPayment}
            variant="contained"
            disabled={calculateTotalPayment() <= 0 || calculateTotalPayment() > remainingBalance || loading}
            startIcon={loading ? <CircularProgress size={20} /> : <PaymentIcon />}
            sx={{
              borderRadius: '12px',
              px: 4,
              py: 1,
              fontWeight: 600,
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' },
              '&.Mui-disabled': { background: alpha('#10b981', 0.4) }
            }}
          >
            {loading ? 'Processing...' : 'Record Payment'}
          </Button>
        )}
        {activeTab === 1 && hasFinePending && (
          <Button
            onClick={handleSubmitFinePayment}
            variant="contained"
            disabled={!fineWeight || !finePurity || calculatedFine <= 0 || calculatedFine > remainingFineBalance || fineLoading}
            startIcon={fineLoading ? <CircularProgress size={20} /> : <ScaleIcon />}
            sx={{
              borderRadius: '12px',
              px: 4,
              py: 1,
              fontWeight: 600,
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)' },
              '&.Mui-disabled': { background: alpha('#f59e0b', 0.4) }
            }}
          >
            {fineLoading ? 'Processing...' : 'Record Fine Collection'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// LinearProgress component (if not already imported)
const LinearProgress = ({ variant, value, sx }) => (
  <Box sx={{ ...sx, overflow: 'hidden' }}>
    <Box
      sx={{
        width: `${value}%`,
        height: '100%',
        transition: 'width 0.3s ease',
        borderRadius: 'inherit',
      }}
    />
  </Box>
);

export default PaymentManagementDialog;